/**
 * AddressAutocomplete — Input cu autocomplete Google Places (restricționat RO).
 * Folosit pentru a colecta start_location / end_location pe foile de parcurs.
 */
import { useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2, AlertCircle } from "lucide-react";
import { useGoogleMaps } from "@/utils/googleMaps";

export default function AddressAutocomplete({
  value,
  onChange,
  placeholder = "Tastează adresa...",
  className = "",
  testId,
  disabled = false,
  inputProps = {},
}) {
  const { loaded, error } = useGoogleMaps();
  const inputRef = useRef(null);
  const acRef = useRef(null);

  useEffect(() => {
    if (!loaded || !inputRef.current || acRef.current) return;
    try {
      const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: "ro" },
        fields: ["formatted_address", "name", "place_id"],
      });
      acRef.current = ac;
      const listener = ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        const text = place?.formatted_address || place?.name || inputRef.current.value;
        if (text) onChange(text);
      });
      return () => {
        if (listener && listener.remove) listener.remove();
      };
    } catch (e) {
      // Silent fail — fallback to plain input
      console.error("Autocomplete init failed:", e);
    }
  }, [loaded, onChange]);

  return (
    <div className={`relative ${className}`}>
      <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
      <Input
        ref={inputRef}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={loaded ? placeholder : "Se încarcă harta..."}
        disabled={disabled}
        className="pl-8"
        data-testid={testId}
        {...inputProps}
      />
      {!loaded && !error && (
        <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 animate-spin" />
      )}
      {error && (
        <span
          title="Google Maps indisponibil — poți tasta manual adresa"
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-amber-500"
        >
          <AlertCircle className="w-3.5 h-3.5" />
        </span>
      )}
    </div>
  );
}
